import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";

import { createAxiosInstance } from "api/axiosConfig";

export const OUTSTANDING_LAB_TESTS_QUERY_KEY = "outstandingLabTests";

// A test that has not come back is a slow-moving fact — results arrive on a ~15 minute
// background poller — but the worklist is where a GP acts on one, so it must not show a
// test that was resulted while they were reading it.
const STALE_TIME_MS = 1000 * 30;

/**
 * Every eLab test in the practice that is still waiting for its final result
 * (KIN-2087), oldest first, as the backend's worklist serves it.
 *
 * The three filters the worklist offers are applied in the table, not here: the whole
 * waiting list arrives in one unpaginated response, and the filter options (ordering GP,
 * lab section) are derived from it — asking the server to filter would shrink the very
 * list the options come from, so picking one GP would delete every other GP from the
 * menu.
 *
 * `overdueOnly` is the exception and does go to the server. It is what the sidebar badge
 * needs, and that count must not pull the whole worklist down on every page.
 */
export const useOutstandingLabTests = ({
  overdueOnly = false,
  enabled = true,
  refetchInterval,
} = {}) => {
  const tenantVal =
    useSelector((state) => state.auth.user?.tenant) ||
    localStorage.getItem("kinesin-tenant");

  return useQuery({
    queryKey: [OUTSTANDING_LAB_TESTS_QUERY_KEY, tenantVal, overdueOnly],
    queryFn: async () => {
      const axiosInstance = await createAxiosInstance();
      const response = await axiosInstance.get(
        `${tenantVal}/healthlink/lab-orders/outstanding?overdueOnly=${overdueOnly}`
      );

      // The ApiResponse envelope is unwrapped by the axios interceptor, leaving the
      // list itself.
      return Array.isArray(response.data) ? response.data : [];
    },
    enabled: enabled && !!tenantVal,
    staleTime: STALE_TIME_MS,
    refetchInterval,
    meta: { errorMessage: "Error fetching outstanding lab tests" },
  });
};
